import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { collection, query, orderBy, getDocs, doc, writeBatch, where } from 'firebase/firestore';
import { Logo } from '../components/Logo';

function BifrostConsole() {
  const navigate = useNavigate();
  const { currentUser, userData, logout } = useAuth();
  const [books, setBooks] = useState([]);
  const [selectedBooks, setSelectedBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all'); // 'all', 'books', 'ephemera'
  const [duplicateBarcodes, setDuplicateBarcodes] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadBooks();
  }, [currentUser, filterStatus, filterType]);

  // Check for duplicate barcodes whenever books change
  useEffect(() => {
    if (books.length > 0) {
      const barcodeCount = {};
      books.forEach(book => {
        const barcode = book.barcode;
        if (barcode) {
          barcodeCount[barcode] = (barcodeCount[barcode] || 0) + 1;
        }
      });
      
      const duplicates = Object.entries(barcodeCount)
        .filter(([barcode, count]) => count > 1)
        .map(([barcode, count]) => ({
          barcode,
          count,
          books: books.filter(b => b.barcode === barcode)
        }));
      
      setDuplicateBarcodes(duplicates);
      
      if (duplicates.length > 0) {
        console.warn(`⚠️ Found ${duplicates.length} duplicate barcodes:`, duplicates.map(d => d.barcode));
      }
    }
  }, [books]);

  const loadBooks = async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      const booksRef = collection(db, 'users', currentUser.uid, 'books');
      let booksQuery;
      
      if (filterStatus === 'pending') {
        booksQuery = query(
          booksRef, 
          where('status', 'in', ['pending', 'cataloged']),
          orderBy('createdAt', 'desc')
        );
      } else if (filterStatus === 'exported') {
        booksQuery = query(
          booksRef,
          where('status', '==', 'exported'),
          orderBy('createdAt', 'desc')
        );
      } else {
        booksQuery = query(booksRef, orderBy('createdAt', 'desc'));
      }
      
      const snapshot = await getDocs(booksQuery);
      let booksData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Filter by item type (books vs ephemera)
      if (filterType === 'books') {
        booksData = booksData.filter(book => book.itemType !== 'EPHEMERA');
      } else if (filterType === 'ephemera') {
        booksData = booksData.filter(book => book.itemType === 'EPHEMERA');
      }

      setBooks(booksData);
      setSelectedBooks(booksData.map(book => book.id));
    } catch (error) {
      console.error('Error loading books:', error);
    }
    setLoading(false);
  };

  const toggleBookSelection = (bookId) => {
    setSelectedBooks(prev => 
      prev.includes(bookId) 
        ? prev.filter(id => id !== bookId)
        : [...prev, bookId]
    );
  };

  const exportMARCServerSide = async () => {
    if (selectedBooks.length === 0) {
      alert('Please select at least one book to export');
      return;
    }
    
    setExporting(true);
    
    try {
      // Filter selected books, excluding copy-only books (no MARC generation needed)
      const selectedBooksData = books
        .filter(book => selectedBooks.includes(book.id))
        .filter(book => !book.copyOnly);

      if (selectedBooksData.length === 0) {
        alert('No books selected for MARC generation (copy-only books are excluded)');
        return;
      }

      const marcData = {
        books: selectedBooksData.map(book => ({
          barcode: book.barcode,
          title: book.title,
          subtitle: book.subtitle,
          author: book.author && book.author !== 'Unknown Author' ? book.author : null,
          rawAuthor: book.rawAuthor,
          isbn: book.isbn || book.isbn13,
          isbn10: book.isbn10,
          isbn13: book.isbn13,
          publisher: book.publisher,
          publishDate: book.publishDate || book.year,
          callNumber: book.callNumber,
          location: book.location,
          isOversized: book.isOversized || false,
          deweyDecimal: book.deweyDecimal,
          pages: book.pages || book.pageCount,
          physicalDesc: book.physicalDesc || book.physicalDescription,
          physicalFormat: book.physicalFormat,
          bookFormat: book.bookFormat,
          description: book.description,
          subjects: book.subjects || book.categories || [],
          genres: book.genres || [],
          indexTerms: book.indexTerms || [],
          series: book.series,
          seriesNumber: book.seriesNumber,
          subjectPerson: book.subjectPerson,
          targetAudience: book.targetAudience,
          ageRange: book.ageRange,
          recommendedAge: book.recommendedAge,
          lexileLevel: book.lexileLevel,
          readingLevel: book.readingLevel,
          studyPrograms: book.studyPrograms || [],
          awards: book.awards || [],
          copyNotes: book.copyNotes || [],
          coAuthors: book.coAuthors || [],
          generalNotes: book.generalNotes || [],
          notes: book.notes,
          language: book.language || 'eng',
          coverUrl: book.coverUrl,
          libraryPrice: book.libraryPrice,
          listPrice: book.listPrice,
          itemType: book.itemType || 'BOOK'
        })),
        schoolInfo: {
          name: userData?.schoolName || 'HEIMDL Library',
          code: userData?.schoolName?.replace(/[^A-Z]/g, '').substring(0, 6) || 'HEIMDL'
        }
      };
      
      const serverUrl = window.location.hostname === 'localhost'
  ? 'http://localhost:3001'
  : 'https://marc.heimdlcatalog.com';

      // Use ephemera endpoint if exporting only ephemera items
      const hasEphemera = selectedBooksData.some(book => book.itemType === 'EPHEMERA');
      const allEphemera = selectedBooksData.every(book => book.itemType === 'EPHEMERA');
      const endpoint = allEphemera ? '/api/generate-ephemera-marc' : '/api/generate-marc';

      if (hasEphemera && !allEphemera) {
        console.log('Mixed export: includes both books and ephemera - using standard MARC endpoint');
      }

      // Build request body with correct key for endpoint
      const requestBody = allEphemera
        ? { items: marcData.books, schoolInfo: marcData.schoolInfo }
        : marcData;

      const response = await fetch(`${serverUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }
      
      const blob = await response.blob();
      const generatedRecords = response.headers.get('X-Generated-Records');
      const errorCount = response.headers.get('X-Error-Count') || '0';
      
      const today = new Date();
      const dateString = today.toISOString().slice(0, 10).replace(/-/g, '');
      const timeString = today.toTimeString().slice(0, 5).replace(':', '');
      const schoolCode = userData?.schoolName?.substring(0, 8)
        .toLowerCase().replace(/[^a-z]/g, '') || 'heimdl';
      const filename = `${schoolCode}_${dateString}_${timeString}.mrc`;
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      const batch = writeBatch(db);
      selectedBooksData.forEach(book => {
        const bookRef = doc(db, 'users', currentUser.uid, 'books', book.id);
        batch.update(bookRef, { 
          status: 'exported',
          exportedAt: new Date(),
          exportedFile: filename,
          exportMethod: 'production-server'
        });
      });
      await batch.commit();
      
      const warningText = parseInt(errorCount) > 0 
        ? `\n\n${errorCount} books had minor issues but were still processed.`
        : '';
      
      alert(`MARC Export Successful!\n\nGenerated: ${generatedRecords || selectedBooks.length} MARC records\nFile: ${filename}${warningText}\n\nReady for LibraryWorld import.`);
      
      await loadBooks();
      setSelectedBooks([]);
      
    } catch (error) {
      console.error('MARC export error:', error);
      
      let errorMessage = 'MARC Export Failed\n\n';
      
      if (error.message.includes('fetch') || error.message.includes('Failed to fetch')) {
        errorMessage += 'Cannot connect to MARC server.\n\nMake sure the server is running: node server.js\nCheck if http://localhost:3001/api/health responds';
      } else {
        errorMessage += `Error: ${error.message}\n\nPlease check that all selected books have titles and barcodes.`;
      }
      
      alert(errorMessage);
    }
    
    setExporting(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-gradient-to-r from-purple-900 to-indigo-900 text-white shadow-xl border-b border-purple-700/50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <Logo size={40} showText={false} />
              <button onClick={() => navigate('/asgard')} className="text-lg sm:text-2xl font-bold hover:text-purple-200 tracking-wider">
                HEIMDL
              </button>
              <span className="hidden sm:inline text-purple-300">|</span>
              <span className="hidden sm:inline text-purple-200">The Bifrost Console</span>
            </div>
            <div className="flex items-center space-x-3 sm:space-x-6">
              <div className="text-right">
                <p className="text-sm text-purple-300">Ready for Export</p>
                <p className="text-xl font-bold">{books.filter(book => book.status !== 'exported').length}</p>
              </div>
              <button
                onClick={handleLogout}
                className="text-sm sm:text-base bg-purple-700 hover:bg-purple-600 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg transition-colors"
              >
                Leave Asgard
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="space-y-6">
          <div className="bg-slate-200 rounded-xl shadow-xl p-6 border border-slate-300">
            <h1 className="text-2xl font-bold mb-4 text-slate-800">Export to LibraryWorld</h1>
            <p className="text-slate-600 mb-6">Generate MARC records for LibraryWorld import</p>
            
            {/* Duplicate Barcode Warning */}
            {duplicateBarcodes.length > 0 && (
              <div className="bg-red-100 border-2 border-red-400 text-red-800 px-4 py-3 rounded-lg mb-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-bold text-lg">⚠️ Duplicate Barcodes Detected!</p>
                    <p className="text-sm mt-1">
                      Found {duplicateBarcodes.length} duplicate barcode{duplicateBarcodes.length > 1 ? 's' : ''}: {' '}
                      <span className="font-mono font-bold">
                        {duplicateBarcodes.map(d => `${d.barcode} (×${d.count})`).join(', ')}
                      </span>
                    </p>
                    <p className="text-sm mt-2">
                      Export is disabled until duplicates are resolved. Each book must have a unique barcode for LibraryWorld.
                    </p>
                  </div>
                  <button
                    onClick={() => navigate('/allsight?duplicates=true')}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 whitespace-nowrap ml-4"
                  >
                    Fix in Allsight →
                  </button>
                </div>
              </div>
            )}
            
            <div className="mb-6 flex flex-wrap gap-2">
              <button
                onClick={() => navigate('/forge')}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors text-sm"
              >
                Back to Forge
              </button>
              <button
                onClick={() => navigate('/runes')}
                className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors text-sm"
              >
                Print Labels
              </button>
              <button
                onClick={() => navigate('/allsight')}
                className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors text-sm"
              >
                Browse Books
              </button>
            </div>
            
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search by title, author, barcode, call number..."
                className="w-full px-4 py-2 border border-slate-300 rounded-lg bg-white"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Item Type:
                </label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                >
                  <option value="all">All Items</option>
                  <option value="books">Books Only</option>
                  <option value="ephemera">Ephemera Only</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Export Status:
                </label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                >
                  <option value="all">All</option>
                  <option value="pending">Ready for Export</option>
                  <option value="exported">Previously Exported</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Selected:
                </label>
                <p className="text-lg font-bold text-purple-600 py-2">
                  {selectedBooks.length} of {books.length}
                </p>
              </div>

              <div className="flex items-end">
                <button
                  onClick={exportMARCServerSide}
                  disabled={exporting || selectedBooks.length === 0 || duplicateBarcodes.length > 0}
                  className={`w-full px-4 py-2 rounded-lg font-medium flex items-center justify-center space-x-2 ${
                    duplicateBarcodes.length > 0 
                      ? 'bg-red-400 text-white cursor-not-allowed' 
                      : 'bg-green-600 text-white hover:bg-green-700 disabled:bg-slate-400'
                  }`}
                  title={duplicateBarcodes.length > 0 ? 'Fix duplicate barcodes before exporting' : ''}
                >
                  {exporting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span className="text-sm">Generating...</span>
                    </>
                  ) : duplicateBarcodes.length > 0 ? (
                    <span className="text-sm">⚠️ Fix Duplicates First</span>
                  ) : (
                    <span className="text-sm">Export MARC</span>
                  )}
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="bg-slate-200 rounded-xl shadow-xl p-8 border border-slate-300 text-center">
              <p className="text-slate-600">Loading books for export...</p>
            </div>
          ) : books.length === 0 ? (
            <div className="bg-slate-200 rounded-xl shadow-xl p-8 border border-slate-300 text-center">
              <p className="text-slate-600 mb-4">No books found for export.</p>
              <button 
                onClick={() => navigate('/forge')}
                className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700"
              >
                Visit the Forge
              </button>
            </div>
          ) : (
            <div className="bg-slate-200 rounded-xl shadow-xl border border-slate-300">
              <div className="px-6 py-4 border-b border-slate-300 flex justify-between items-center bg-gradient-to-r from-slate-300 to-slate-250 rounded-t-xl">
                <h2 className="text-xl font-bold text-slate-800">Select Books for MARC Export</h2>
                <div className="space-x-2">
                  <button
                    onClick={() => setSelectedBooks(books.map(book => book.id))}
                    className="text-purple-600 hover:text-purple-500 text-sm"
                  >
                    Select All
                  </button>
                  <button
                    onClick={() => setSelectedBooks([])}
                    className="text-red-600 hover:text-red-500 text-sm"
                  >
                    Clear All
                  </button>
                </div>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {books.filter(book => {
                  if (!searchTerm) return true;
                  const term = searchTerm.toLowerCase();
                  return (
                    book.title?.toLowerCase().includes(term) ||
                    book.author?.toLowerCase().includes(term) ||
                    book.barcode?.includes(term) ||
                    book.callNumber?.toLowerCase().includes(term) ||
                    book.isbn?.includes(term) ||
                    book.location?.toLowerCase().includes(term)
                  );
                }).map((book) => (
                  <div key={book.id} className="px-6 py-3 border-b border-slate-300 last:border-b-0 hover:bg-slate-150">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={selectedBooks.includes(book.id)}
                        onChange={() => toggleBookSelection(book.id)}
                        className="h-4 w-4 text-purple-600"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-slate-800">{book.title}</p>
                        <p className="text-sm text-slate-600">
                          {book.author} | {book.barcode} | {book.callNumber} | {book.location}
                          {book.copyOnly && (
                            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Copy Only
                            </span>
                          )}
                          {book.itemType === 'EPHEMERA' && (
                            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800">
                              Ephemera
                            </span>
                          )}
                          {book.status === 'exported' && (
                            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Exported
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default BifrostConsole;